/**
 * Feature gating service — checks if a feature is enabled for a user.
 * Routes through apiRequest which uses Boson postMessage in embedded environments.
 */
import { apiRequest } from '../apiMiddleware';
import { getUserName } from '../boson/bosonClient';
import { API_GATEWAY_PROCESS_NAME } from '../../utils/constants';
import {
  logOperationalMetric,
  MetricType,
} from '../../utils/metric-utils';

export interface IsFeatureEnabledRequest {
  featureName: string;
  userId: string;
  warehouseId: string;
}

export interface IsFeatureEnabledResponse {
  featureEnabled: boolean;
  featureName: string;
}

const IS_FEATURE_ENABLED_API = 'isFeatureEnabled';
const RCC_AGENT_CHAT_PAGE = 'RCC_AGENT_CHAT';

/**
 * Check if a feature is enabled for the current user.
 * Resolves the user alias via Boson and calls the IsFeatureEnabled facade operation.
 *
 * @param featureName - The feature to check (e.g. 'RCC_AGENT_CHAT')
 * @returns Whether the feature is enabled, or false on failure
 */
export const isFeatureEnabled = async (featureName: string): Promise<boolean> => {
  const startTime = Date.now();
  try {
    const userId = await getUserName();

    if (!userId) {
      console.error('[featureEnabledService] User ID not resolved, cannot check feature access');
      return false;
    }

    const requestBody: IsFeatureEnabledRequest = {
      featureName,
      userId,
      warehouseId: '',
    };

    const requestPayload = {
      resourcePath: '/isFeatureEnabled',
      httpMethod: 'post',
      processName: API_GATEWAY_PROCESS_NAME,
      requestBody,
    };

    const response = await apiRequest<IsFeatureEnabledResponse>(requestPayload);

    logOperationalMetric(
      startTime,
      '',
      RCC_AGENT_CHAT_PAGE,
      IS_FEATURE_ENABLED_API,
      MetricType.SUCCESS
    );

    return response.data?.featureEnabled ?? false;
  } catch (error: any) {
    console.error('[featureEnabledService] IsFeatureEnabled call failed:', error);
    logOperationalMetric(
      startTime,
      '',
      RCC_AGENT_CHAT_PAGE,
      IS_FEATURE_ENABLED_API,
      MetricType.FAILED,
      error
    );
    return false;
  }
};
